import { z } from "zod";
import {
  PUBLIC_DATASET_AGENT_NAME,
  type RunStatusAgent,
  VCF_INGEST_AGENT_NAME,
} from "~/services/dagRuns";
import type { ApprovalCardSource } from "./ApprovalCardTool";
import { parseToolBody } from "./parseToolBody";
import type { ToolCallEntry } from "./useTurns";

// The approval card serves both the public-dataset and VCF ingest trigger
// tools; each toolkit has its own list_pipelines companion.
export const PUBLIC_DATASET_TRIGGER_RUN_TOOL_NAME = "public_dataset_trigger_run";
export const VCF_TRIGGER_RUN_TOOL_NAME = "vcf_ingest_trigger_run";
export const PUBLIC_DATASET_LIST_PIPELINES_TOOL_NAME = "public_dataset_list_pipelines";
export const VCF_LIST_PIPELINES_TOOL_NAME = "vcf_ingest_list_pipelines";
export const LIST_PIPELINES_TOOL_NAMES: ReadonlySet<string> = new Set([
  PUBLIC_DATASET_LIST_PIPELINES_TOOL_NAME,
  VCF_LIST_PIPELINES_TOOL_NAME,
]);
// Which agent's run-status route a run triggered by each tool is polled on.
export const TRIGGER_TOOL_AGENTS: ReadonlyMap<string, RunStatusAgent> = new Map([
  [PUBLIC_DATASET_TRIGGER_RUN_TOOL_NAME, PUBLIC_DATASET_AGENT_NAME],
  [VCF_TRIGGER_RUN_TOOL_NAME, VCF_INGEST_AGENT_NAME],
]);

// Trigger args: dag_id plus any declared-param overrides the user already
// consented to in chat. Not exported — only `parseTriggerArgs` needs it.
const TriggerArgsSchema = z.object({
  dag_id: z.string().min(1),
  // A pipeline's declared params are not all booleans — the trigger tool
  // accepts any value matching the Param's own JSON type, so an unparsed
  // override would render a card with no approve/reject control.
  dag_params: z
    .record(z.string(), z.union([z.boolean(), z.number(), z.string(), z.array(z.string())]))
    .optional(),
});

interface TriggerArgs {
  dagId: string;
  dagParams: Record<string, boolean | number | string | string[]>;
}

export function parseTriggerArgs(args: string): TriggerArgs | null {
  const view = parseToolBody(args);
  if (view.kind !== "json" || Array.isArray(view.value)) return null;
  const parsed = TriggerArgsSchema.safeParse(view.value);
  if (!parsed.success) return null;
  return { dagId: parsed.data.dag_id, dagParams: parsed.data.dag_params ?? {} };
}

// Not exported — only `resolveDagDisplayName` below needs it.
const ListedPipelineSchema = z.object({
  dag_id: z.string(),
  dag_display_name: z.string().optional(),
});

// Never trust the trigger call's own args for the display name (model
// input) — correlate the turn's list_pipelines result instead, mirroring
// a sibling tool result in the same turn (either toolkit's). Last match wins.
export function resolveDagDisplayName(
  toolCalls: readonly ToolCallEntry[] | undefined,
  dagId: string
): string | undefined {
  if (!toolCalls) return undefined;
  const listings = toolCalls.filter((tc) => LIST_PIPELINES_TOOL_NAMES.has(tc.name) && tc.result);
  for (let i = listings.length - 1; i >= 0; i--) {
    const view = parseToolBody(listings[i].result);
    if (view.kind !== "json" || !Array.isArray(view.value)) continue;
    for (const raw of view.value) {
      const parsed = ListedPipelineSchema.safeParse(raw);
      if (parsed.success && parsed.data.dag_id === dagId) {
        return parsed.data.dag_display_name;
      }
    }
  }
  return undefined;
}

// The display name comes from the turn's listing, falling back to `dag_id`.
export const triggerRunApproval: ApprovalCardSource = {
  toCard: (rawArgs, turnToolCalls) => {
    const args = parseTriggerArgs(rawArgs);
    if (!args) return null;
    const label = resolveDagDisplayName(turnToolCalls, args.dagId) ?? args.dagId;
    const params = Object.entries(args.dagParams);
    return {
      title: `Trigger ${label}?`,
      metadata: [{ key: "Pipeline", value: label }],
      sections:
        params.length > 0
          ? [
              {
                heading: "Parameter overrides",
                items: params.map(([key, value]) => ({
                  label: key,
                  value: Array.isArray(value) ? value.join(", ") : String(value),
                })),
              },
            ]
          : undefined,
      confirmLabel: "Trigger",
    };
  },
  pendingLabel: "Preparing pipeline trigger…",
  errorLabel: "Couldn't load this trigger request.",
};
